
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { Calendar as CalendarIcon, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';

interface BlackoutDatesManagerProps {
  dates: string[];
  onAdd: (date: string) => void;
  onRemove: (date: string) => void;
  disabled?: boolean;
}

export function BlackoutDatesManager({
  dates,
  onAdd,
  onRemove,
  disabled,
}: BlackoutDatesManagerProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>();

  const handleAddDate = () => {
    if (selectedDate) {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      if (!dates.includes(dateStr)) {
        onAdd(dateStr);
      }
      setSelectedDate(undefined);
      setIsOpen(false);
    }
  };

  const sortedDates = [...dates].sort();

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {sortedDates.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t('classes.noBlackoutDates', 'No blackout dates set')}
          </p>
        ) : (
          sortedDates.map((date) => (
            <Badge key={date} variant="secondary" className="gap-2">
              {format(new Date(date + 'T00:00:00'), 'MMM dd, yyyy')}
              {!disabled && (
                <button
                  onClick={() => onRemove(date)}
                  className="ml-1 hover:text-destructive"
                  type="button"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </Badge>
          ))
        )}
      </div>

      {!disabled && (
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm">
              <CalendarIcon className="h-4 w-4 mr-2" />
              {t('classes.addBlackoutDate', 'Add Blackout Date')}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={setSelectedDate}
              initialFocus
            />
            <div className="p-3 border-t">
              <Button
                onClick={handleAddDate}
                disabled={!selectedDate}
                className="w-full"
                size="sm"
              >
                {t('common.add', 'Add')}
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
